import { msg } from '@lingui/core/macro'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { ProjectDetail } from '~/components/projects/project-detail'
import { decodeTasksSearch, type TasksSearch } from '~/components/tasks/search'
import { titleFor } from '~/lib/route-title'
import { optionalString } from '~/lib/search-params'
import type { Id } from '~convex/_generated/dataModel'

type LegacyProjectTasksDestination = {
  params: { projectId: string; workspaceSlug: string }
  search: TasksSearch & { task?: Id<'tasks'> }
  to: '/$workspaceSlug/projects/$projectId/tasks'
}

/**
 * Preserves old `?tab=tasks` links after Tasks became a real child route.
 * The Tasks decoder is the boundary: valid board filters, presentation and
 * text search survive; project-only search keys and unknown values do not.
 * `task` is inherited from the workspace layout rather than owned by that
 * decoder, so preserve it separately to keep a docked task open.
 */
export const legacyProjectTasksDestination = (
  params: { projectId: string; workspaceSlug: string },
  search: Record<string, unknown>
): LegacyProjectTasksDestination | null => {
  if (search.tab !== 'tasks') {
    return null
  }
  return {
    params,
    search: {
      ...decodeTasksSearch(search),
      // The child board is already hard-scoped to this project.
      projects: undefined,
      task: optionalString<Id<'tasks'>>(search.task),
    },
    to: '/$workspaceSlug/projects/$projectId/tasks',
  }
}

/**
 * The project-detail LAYOUT: identity and header, with each destination —
 * Overview, Rooms, Renders, … — a child route rendered into its outlet. The
 * destinations used to be a `?tab` search param on this route; they are real
 * pages now, so the router owns active states and each page owns its own
 * search params.
 */
const ProjectDetailRoute = () => {
  const { projectId } = Route.useParams()

  return <ProjectDetail id={projectId as Id<'projects'>} key={projectId} />
}

export const Route = createFileRoute('/_authenticated/$workspaceSlug/projects/$projectId')({
  beforeLoad: ({ location, params }) => {
    // Child routes no longer validate `tab`; inspect the raw parsed search so
    // bookmarks from the former tabbed page retain their task-board state.
    const destination = legacyProjectTasksDestination(params, location.search)
    if (destination !== null) {
      throw redirect(destination)
    }
  },
  component: ProjectDetailRoute,
  head: () => ({ meta: [{ title: titleFor(msg`Project`) }] }),
})
